import { useMemo } from 'react';
import useSWR from 'swr';

import { SingSetup, Song } from '~/interfaces';
import { fetchSongBoard, songBoardUrl } from '~/modules/leaderboard/client';
import LeaderboardRow from '~/modules/leaderboard/leaderboard-row';
import { slotIntoBoard } from '~/modules/leaderboard/slot-into-board';
import { BoardEntry } from '~/modules/leaderboard/types';
import ScoreboardPanel from '~/modules/scoreboard/scoreboard-panel';
import { LeaderboardPostGame } from '~/routes/game/singing/post-game/views/leaderboard/use-leaderboard-post-game';

interface Props {
  song: Song;
  singSetup: SingSetup;
  leaderboard: LeaderboardPostGame;
  className?: string;
  /** Passed straight through to the panel's scrolling list — see `ScoreboardPanel`. */
  listClassName?: string;
}

/**
 * The global board for the song just sung, sitting beside the local high scores.
 *
 * Split by difficulty and nothing else — the vocal track is deliberately not part of the split, so
 * both singers of a duet are ranked together. That split is also why Easy belongs here while it is
 * kept off the main menu's board: ranked only against other Easy runs of the same song, it is
 * comparing like with like.
 *
 * Rendering does not depend on the score qualifying: a player who is nowhere near the board still
 * gets told where they would have landed, which is the only reason to show it to them at all.
 */
function SongLeaderboardPanel({ song, singSetup, leaderboard, className, listClassName }: Props) {
  const { hasLeaderboard, difficulty, score, name, country, hasSubmitted } = leaderboard;

  const shouldFetch = hasLeaderboard;

  const { data, error, isLoading } = useSWR(
    shouldFetch ? songBoardUrl({ songId: song.id, tolerance: singSetup.tolerance, score }) : null,
    fetchSongBoard,
    { revalidateOnFocus: false },
  );

  /**
   * The board as the player should read it: the window the Worker returned with the run just sung
   * slotted into place. The row is synthetic — the score has usually not been submitted yet, and
   * after it has, nothing refetches.
   */
  const slotted = useMemo(() => {
    if (!data) return null;

    const player: BoardEntry = {
      name: name.trim() || 'You',
      country: country || null,
      score,
      artist: song.artist,
      title: song.title,
      songId: song.id,
      tolerance: singSetup.tolerance,
      createdAt: Date.now(),
    };

    // Once it's being shared, a board that arrives late may already hold it; before, a row it ties
    // is an earlier submission, not this run
    return slotIntoBoard(data, player, { matchShared: hasSubmitted });
  }, [data, name, country, score, song, singSetup.tolerance, hasSubmitted]);
  const rows = slotted?.rows ?? [];

  // Away entirely for the dev-only debug widths above Easy — those are never stored, so the list
  // would be permanently empty.
  if (!shouldFetch) return null;

  return (
    <ScoreboardPanel
      className={className}
      listClassName={listClassName}
      title="Global scoreboard"
      // The count the removed "of N" sentence used to carry — the player's own row says the rest
      subtitle={`This song · ${difficulty} · all time${data ? ` · ${data.total + (slotted?.inserted ? 1 : 0)} scores` : ''}`}
      isLoading={isLoading}
      error={error}
      isEmpty={rows.length === 0}
      emptyMessage="Nobody has shared a score for this song yet"
      data-test="song-leaderboard-panel">
      {rows.map(({ entry, isPlayer }, index) => (
        <LeaderboardRow
          key={`${entry.name}-${entry.score}-${index}`}
          entry={entry}
          position={(data?.startPosition ?? 1) + index}
          withSongDetails={false}
          highlighted={isPlayer}
          scrollIntoView={isPlayer}
          data-test={isPlayer ? 'song-leaderboard-own-row' : 'song-leaderboard-row'}
        />
      ))}
    </ScoreboardPanel>
  );
}

export default SongLeaderboardPanel;
